import { Injectable, signal, computed, inject, effect } from '@angular/core';
import { CallStateStore } from './call/call-state.store';
import { CallState } from '../../types/telnyx-webrtc';

/**
 * Panel position coordinates
 */
export interface PanelPosition {
  x: number;
  y: number;
}

/**
 * Panel dimensions for boundary calculations
 */
export interface PanelDimensions {
  width: number;
  height: number;
  minimizedWidth: number;
  minimizedHeight: number;
}

const STORAGE_KEY = 'globalCallPanelPosition';
const DEFAULT_POSITION: PanelPosition = { x: -1, y: -1 }; // -1 means "use default bottom-right"
const PANEL_DIMENSIONS: PanelDimensions = {
  width: 380,
  height: 280,
  minimizedWidth: 200,
  minimizedHeight: 56,
};

// Active call states that should show the panel
const ACTIVE_CALL_STATES: CallState[] = ['dialing', 'ringing', 'connecting', 'connected', 'held'];

/**
 * GlobalCallPanelService
 *
 * Manages the visibility, position, and minimize state of the global floating call panel.
 *
 * Key responsibilities:
 * - Show/hide panel based on call state
 * - Manage minimized/expanded state
 * - Persist and restore panel position
 * - Provide reactive state for the panel component
 *
 * The panel auto-shows when a call becomes active and auto-hides when idle.
 * Users can manually close/minimize the panel during a call.
 */
@Injectable({
  providedIn: 'root'
})
export class GlobalCallPanelService {
  private callStore = inject(CallStateStore);

  // ============================================================================
  // State Signals
  // ============================================================================

  /** Whether panel is currently visible */
  private _isVisible = signal<boolean>(false);

  /** Whether panel is in minimized state */
  private _isMinimized = signal<boolean>(false);

  /** Panel position (x, y coordinates) */
  private _position = signal<PanelPosition>(this.loadSavedPosition());

  /** Whether user manually closed the panel (prevents auto-show) */
  private _userClosed = signal<boolean>(false);

  /** Flag to suppress auto-show for inbound calls (modal handles ringing state) */
  private _suppressForInbound = signal<boolean>(false);

  /** Flag to suppress auto-show while confirmation dialog is open */
  private _dialogOpen = signal<boolean>(false);

  // ============================================================================
  // Public Signals (readonly)
  // ============================================================================

  /** Whether the floating panel should be visible */
  readonly isVisible = this._isVisible.asReadonly();

  /** Whether the panel is minimized */
  readonly isMinimized = this._isMinimized.asReadonly();

  /** Current panel position */
  readonly position = this._position.asReadonly();

  /** Panel dimensions for component use */
  readonly dimensions = signal<PanelDimensions>(PANEL_DIMENSIONS);

  /** Computed: current dimensions based on minimized state */
  readonly currentDimensions = computed(() => {
    const dims = this.dimensions();
    const minimized = this._isMinimized();
    return {
      width: minimized ? dims.minimizedWidth : dims.width,
      height: minimized ? dims.minimizedHeight : dims.height,
    };
  });

  /** Whether panel can be shown (has active call) */
  readonly canShow = computed(() => {
    const state = this.callStore.callState();
    return ACTIVE_CALL_STATES.includes(state);
  });

  constructor() {
    // Setup auto-show/hide based on call state
    effect(() => {
      const state = this.callStore.callState();
      const activeCall = this.callStore.activeCall();
      const userClosed = this._userClosed();
      const suppressInbound = this._suppressForInbound();
      const dialogOpen = this._dialogOpen();

      // Determine if we should show the panel
      const isActiveCall = ACTIVE_CALL_STATES.includes(state);
      const isOutbound = activeCall?.direction === 'outbound';
      const isConnectedInbound = activeCall?.direction === 'inbound' && state === 'connected';

      if (isActiveCall) {
        // For outbound calls: show immediately (unless user closed)
        // For inbound calls: only show after connected (modal handles ringing)
        // Don't auto-show while confirmation dialog is open
        const shouldAutoShow = (isOutbound || isConnectedInbound) && !userClosed && !suppressInbound && !dialogOpen;

        if (shouldAutoShow && !this._isVisible()) {
          this._isVisible.set(true);
          console.log('📞 [GlobalCallPanel] Auto-showing for', activeCall?.direction, 'call');
        }
      } else if (state === 'idle') {
        // Call ended - reset state
        if (this._isVisible() || dialogOpen) {
          // Small delay to allow for post-call UI transitions
          setTimeout(() => {
            if (this.callStore.callState() === 'idle') {
              this.reset();
            }
          }, 500);
        }
      }
    }, { allowSignalWrites: true });
  }

  // ============================================================================
  // Public Methods - Visibility
  // ============================================================================

  /**
   * Show the floating panel.
   * Called automatically when call becomes active.
   */
  showPanel(): void {
    if (!this.canShow()) {
      console.log('📞 [GlobalCallPanel] Cannot show: no active call');
      return;
    }

    this._isVisible.set(true);
    this._userClosed.set(false);
    console.log('📞 [GlobalCallPanel] Panel shown');
  }

  /**
   * Hide the floating panel.
   * Called automatically when call ends, or manually by user.
   */
  hidePanel(): void {
    this._isVisible.set(false);
    console.log('📞 [GlobalCallPanel] Panel hidden');
  }

  /**
   * User manually closes the panel.
   * Prevents auto-show until call ends.
   */
  closeByUser(): void {
    this._isVisible.set(false);
    this._userClosed.set(true);
    console.log('📞 [GlobalCallPanel] Panel closed by user');
  }

  /**
   * Toggle panel visibility.
   */
  toggleVisibility(): void {
    if (this._isVisible()) {
      this.closeByUser();
    } else {
      this.showPanel();
    }
  }

  /**
   * Set dialog open state to suppress auto-show.
   * Called when confirmation dialog opens/closes.
   */
  setDialogOpen(open: boolean): void {
    this._dialogOpen.set(open);
    console.log('📞 [GlobalCallPanel] Dialog open:', open);
  }

  // ============================================================================
  // Public Methods - Minimize/Expand
  // ============================================================================

  /**
   * Minimize the panel to compact view.
   */
  minimize(): void {
    this._isMinimized.set(true);
    console.log('📞 [GlobalCallPanel] Panel minimized');
  }

  /**
   * Expand the panel to full view.
   */
  expand(): void {
    this._isMinimized.set(false);
    console.log('📞 [GlobalCallPanel] Panel expanded');
  }

  /**
   * Toggle between minimized and expanded states.
   */
  toggleMinimize(): void {
    this._isMinimized.update(v => !v);
    console.log('📞 [GlobalCallPanel] Panel minimize toggled:', this._isMinimized());
  }

  // ============================================================================
  // Public Methods - Position
  // ============================================================================

  /**
   * Update panel position.
   * Called when user drags the panel.
   *
   * @param position - New position coordinates
   */
  updatePosition(position: PanelPosition): void {
    const clamped = this.clampToViewport(position);
    this._position.set(clamped);
    this.savePosition(clamped);
  }

  /**
   * Reset position to default (bottom-right corner).
   */
  resetPosition(): void {
    this._position.set(DEFAULT_POSITION);
    this.clearSavedPosition();
    console.log('📞 [GlobalCallPanel] Position reset to default');
  }

  /**
   * Get the effective position for rendering.
   * Handles default position calculation.
   *
   * @returns Position in pixels from top-left
   */
  getEffectivePosition(): { top: number; left: number } {
    const pos = this._position();
    const dims = this.currentDimensions();

    // If default position (-1, -1), calculate center of viewport
    if (pos.x === -1 || pos.y === -1) {
      const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1920;
      const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 1080;

      return {
        left: (viewportWidth - dims.width) / 2,
        top: (viewportHeight - dims.height) / 2,
      };
    }

    return { left: pos.x, top: pos.y };
  }

  // ============================================================================
  // Public Methods - Inbound Call Handling
  // ============================================================================

  /**
   * Suppress auto-show for inbound calls.
   * Called when incoming call modal is displayed.
   */
  suppressForInboundCall(): void {
    this._suppressForInbound.set(true);
    console.log('📞 [GlobalCallPanel] Auto-show suppressed for inbound call');
  }

  /**
   * Allow auto-show after inbound call modal closes.
   * Called when incoming call is answered and modal transitions to panel.
   */
  allowAutoShow(): void {
    this._suppressForInbound.set(false);
    console.log('📞 [GlobalCallPanel] Auto-show allowed');
  }

  /**
   * Transition from incoming call modal to floating panel.
   * Called after user answers an incoming call.
   * Force-shows the panel, bypassing canShow() check since we know the call was answered.
   */
  transitionFromInboundModal(): void {
    this._suppressForInbound.set(false);
    this._userClosed.set(false);
    this._isMinimized.set(false);
    // Force-show the panel - we know the call was answered from the modal
    // Don't call showPanel() which has a canShow() guard that might fail due to timing
    this._isVisible.set(true);
    console.log('📞 [GlobalCallPanel] Transitioned from inbound modal - panel force-shown');
  }

  // ============================================================================
  // Public Methods - State Reset
  // ============================================================================

  /**
   * Reset all panel state.
   * Called when call ends completely.
   */
  reset(): void {
    this._isVisible.set(false);
    this._isMinimized.set(false);
    this._userClosed.set(false);
    this._suppressForInbound.set(false);
    this._dialogOpen.set(false);
    console.log('📞 [GlobalCallPanel] State reset');
  }

  // ============================================================================
  // Private Methods
  // ============================================================================

  /**
   * Load saved position from localStorage.
   */
  private loadSavedPosition(): PanelPosition {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return DEFAULT_POSITION;
    }

    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (typeof parsed.x === 'number' && typeof parsed.y === 'number') {
          // Validate position is reasonable
          if (parsed.x >= 0 && parsed.y >= 0 && parsed.x < 10000 && parsed.y < 10000) {
            return this.clampToViewport(parsed);
          }
        }
      }
    } catch (e) {
      console.warn('📞 [GlobalCallPanel] Failed to load saved position:', e);
    }

    return DEFAULT_POSITION;
  }

  /**
   * Save position to localStorage.
   */
  private savePosition(position: PanelPosition): void {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return;
    }

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(position));
    } catch (e) {
      console.warn('📞 [GlobalCallPanel] Failed to save position:', e);
    }
  }

  /**
   * Clear saved position from localStorage.
   */
  private clearSavedPosition(): void {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return;
    }

    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      console.warn('📞 [GlobalCallPanel] Failed to clear saved position:', e);
    }
  }

  /**
   * Clamp position to stay within viewport bounds.
   */
  private clampToViewport(position: PanelPosition): PanelPosition {
    if (typeof window === 'undefined') {
      return position;
    }

    const dims = this.currentDimensions();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const minX = 0;
    const minY = 0;
    const maxX = Math.max(0, viewportWidth - dims.width - 10);
    const maxY = Math.max(0, viewportHeight - dims.height - 10);

    return {
      x: Math.max(minX, Math.min(position.x, maxX)),
      y: Math.max(minY, Math.min(position.y, maxY)),
    };
  }
}
